import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Activity,
  FileText,
  CheckCircle,
  MessageCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ActivityLog } from '@/types/product-manager';
import { getActivityLogs, getTasks, getProjects } from '@/services/productManagerService';
import { employeeNamesService } from '@/services/employeeNames';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface ActivityFeedProps {
  entityId?: string;
  entityType?: 'project' | 'task' | 'comment';
  limit?: number;
}

interface EnhancedActivityLog extends ActivityLog {
  userDisplayName?: string;
  taskTitle?: string;
  projectName?: string;
  assignedToName?: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  entityId,
  entityType,
  limit = 20
}) => {
  const [allActivities, setAllActivities] = useState<EnhancedActivityLog[]>([]);
  const [activities, setActivities] = useState<EnhancedActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const [activitiesData, tasksData, projectsData] = await Promise.all([
        getActivityLogs(entityId, entityType),
        getTasks(),
        getProjects()
      ]);
      
      // Filter to only show important activities
      const importantActivities = activitiesData.filter(activity => {
        return (
          (activity.entityType === 'task' && activity.action === 'created') ||
          (activity.entityType === 'task' && activity.action === 'completed') ||
          (activity.entityType === 'comment' && activity.action === 'created')
        );
      });

      // Enhance activities with display names and additional context
      const enhancedActivities = await Promise.all(
        importantActivities.map(async (activity) => {
          const displayName = await employeeNamesService.getNameFromEmail(activity.userEmail);
          
          let taskTitle = '';
          let projectName = '';
          let assignedToName = '';
          
          // For comment activities, get the actual task title
          if (activity.entityType === 'comment' && activity.metadata?.taskId) {
            const task = tasksData.find(t => t.id === activity.metadata?.taskId);
            if (task) {
              taskTitle = task.title;
              const project = projectsData.find(p => p.id === task.projectId);
              projectName = project?.name || '';
            }
          } else if (activity.entityType === 'task') {
            // For task activities, always look up the task to get assignedTo info
            const task = tasksData.find(t => t.id === activity.entityId);
            if (task) {
              // Use task title from database as it's most reliable
              taskTitle = task.title;
              const project = projectsData.find(p => p.id === task.projectId);
              projectName = project?.name || '';
              
              // For completed tasks, get the assigned person's name
              if (activity.action === 'completed' && task.assignedTo) {
                assignedToName = await employeeNamesService.getNameFromEmail(task.assignedTo) || task.assignedTo;
              }
            } else {
              // Fallback to metadata if task not found
              taskTitle = activity.metadata?.taskTitle || activity.metadata?.title || '';
              projectName = activity.metadata?.projectName || '';
            }
          }
          
          // Fallback for taskTitle
          if (!taskTitle) {
            taskTitle = `${activity.entityType} ${activity.entityId?.slice(-6)}`;
          }
          
          return {
            ...activity,
            userDisplayName: displayName || activity.userEmail,
            taskTitle,
            projectName,
            assignedToName
          };
        })
      );
      
      setAllActivities(enhancedActivities);
      // Set initial page of activities
      setCurrentPage(0);
      setActivities(enhancedActivities.slice(0, limit));
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, limit]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Update activities when page changes
  useEffect(() => {
    if (allActivities.length > 0) {
      const startIndex = currentPage * limit;
      const endIndex = startIndex + limit;
      setActivities(allActivities.slice(startIndex, endIndex));
    }
  }, [currentPage, allActivities, limit]);

  const totalPages = Math.ceil(allActivities.length / limit);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getActivityIcon = (entityType: string, action: string) => {
    if (entityType === 'comment') return MessageCircle;
    if (action === 'completed') return CheckCircle;
    return FileText;
  };

  const getActivityColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getActivityMessage = (activity: EnhancedActivityLog) => {
    const { entityType, action, taskTitle, projectName } = activity;
    
    if (entityType === 'task' && action === 'created') {
      return `created task "${taskTitle}"${projectName ? ` in ${projectName}` : ''}`;
    }
    
    if (entityType === 'task' && action === 'completed') {
      return `completed task "${taskTitle}"${projectName ? ` in ${projectName}` : ''}`;
    }
    
    if (entityType === 'comment' && action === 'created') {
      return `commented on Task: "${taskTitle}"${projectName ? ` in ${projectName}` : ''}`;
    }
    
    return `${action} ${entityType}`;
  };

  const getActivityUser = (activity: EnhancedActivityLog) => {
    const { entityType, action, assignedToName } = activity;
    
    // For completed tasks, show the assigned person's name instead of the activity creator
    if (entityType === 'task' && action === 'completed' && assignedToName) {
      return assignedToName;
    }
    
    return activity.userDisplayName || activity.userEmail;
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleActivityClick = (activity: EnhancedActivityLog) => {
    // Navigate to task details page if we have the necessary IDs
    if (activity.entityType === 'task' || activity.entityType === 'comment') {
      const taskId = activity.entityType === 'task' ? activity.entityId : activity.metadata?.taskId;
      
      if (taskId) {
        // Use the correct route format: /product-manager/tasks/{taskId}
        navigate(`/product-manager/tasks/${taskId}`);
      }
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex space-x-3 animate-pulse">
                <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Recent Activity
            {allActivities.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {allActivities.length} total
              </Badge>
            )}
          </CardTitle>
          
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={!hasPrevPage}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!hasNextPage}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No recent activity.</p>
            <p className="text-sm">Activity will appear here as team members work on tasks.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const ActivityIcon = getActivityIcon(activity.entityType, activity.action);
              const iconColor = getActivityColor(activity.action);
              const isClickable = activity.entityType === 'task' || activity.entityType === 'comment';
              const displayUser = getActivityUser(activity);
              
              return (
                <div
                  key={activity.id}
                  className={`flex space-x-3 p-3 rounded-lg border ${
                    isClickable
                      ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors'
                      : ''
                  }`}
                  onClick={isClickable ? () => handleActivityClick(activity) : undefined}
                >
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {getInitials(displayUser)}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center border`}>
                        <ActivityIcon className={`h-2.5 w-2.5 ${iconColor}`} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {displayUser}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 ml-1">
                        {getActivityMessage(activity)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTimestamp(activity.timestamp)}
                      </span>
                      {isClickable && (
                        <span className="text-xs text-blue-600 dark:text-blue-400">
                          Click to view details →
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;